import { baseApi } from "api/BaseApi";
import { PaginatedResponse } from "api/common/types/response.types";

import type {
  ProductUpdate,
  ProductUpdateCreatePayload,
  ProductUpdateEditPayload,
  ProductUpdateListFilterParams,
  ProductUpdatePathParams,
} from "./productUpdate.types";

const PRODUCT_BASE_QUERY = "/project/product";

const productUpdateApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // CREATE
    createProductUpdate: build.mutation<
      ProductUpdate,
      ProductUpdateCreatePayload
    >({
      query: ({ parentId, body }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/updates/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PRODUCT_UPDATES"],
    }),

    // LIST
    listProductUpdates: build.query<
      ProductUpdate[],
      ProductUpdateListFilterParams
    >({
      query: ({ parentId, active }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/updates/`,
        params: { active },
      }),
      transformResponse: (result: PaginatedResponse<ProductUpdate>) =>
        result.results,
      providesTags: ["PRODUCT_UPDATES"],
    }),

    // RETRIEVE // NOTE: productUpdate already exists in product.api so adding get avoids namespace collision
    getProductUpdate: build.query<ProductUpdate, ProductUpdatePathParams>({
      query: ({ parentId, updateId }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/updates/${updateId}/`,
      }),
    }),

    // UPDATE
    updateProductUpdate: build.mutation<
      ProductUpdate,
      ProductUpdateEditPayload
    >({
      query: ({ parentId, updateId, body }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/updates/${updateId}/`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["PRODUCT_UPDATES"],
    }),

    // DELETE
    deleteProductUpdate: build.mutation<void, ProductUpdatePathParams>({
      query: ({ parentId, updateId }) => ({
        url: `${PRODUCT_BASE_QUERY}/${parentId}/updates/${updateId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["PRODUCT_UPDATES"],
    }),
  }),

  overrideExisting: false,
});

export const {
  useCreateProductUpdateMutation,
  useGetProductUpdateQuery,
  useListProductUpdatesQuery,
  useUpdateProductUpdateMutation,
  useDeleteProductUpdateMutation,
} = productUpdateApi;
